import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Layers, ChevronRight, ChevronLeft, Plus, X, CheckCircle, Trash2, Save,
  Archive, Upload, FileText, Loader2, AlertTriangle, Clock, Utensils, Pill,
  FlaskConical, Sparkles, Edit3, Eye, Sun, Sunset, Moon, Star, ClipboardPaste,
  Shield, Info, Columns, RotateCcw, ArrowRight
} from 'lucide-react';

const DOMAINS = [
  { id: 'therapeutic_formulations', label: 'Therapeutic Formulations' },
  { id: 'medication', label: 'Medication' },
  { id: 'diet', label: 'Diet' },
  { id: 'lifestyle', label: 'Lifestyle' },
  { id: 'environment', label: 'Environment' },
  { id: 'movement', label: 'Movement' },
  { id: 'mindflo', label: 'MindFlo' },
  { id: 'music_audio', label: 'Music / Audio Support' },
];

const STEPS = [
  { id: 'subscriber', label: 'Subscriber & Phase' },
  { id: 'source', label: 'Protocol Source' },
  { id: 'items', label: 'Protocol Items' },
  { id: 'goals', label: 'Goals & Dates' },
  { id: 'review', label: 'Review & Publish' },
];

const emptyItem = {
  code: '', title: '', domain: 'therapeutic_formulations', timing: '',
  instruction: '', status: 'active', visibility: 'both', linked_timeline_event: '', order: 0
};

const BLOCK_ICONS = { morning: Sun, afternoon: Sunset, evening: Moon, night: Star, anytime: Clock };
const BLOCK_COLORS = {
  morning: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  afternoon: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  evening: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  night: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
  anytime: 'text-white/40 bg-adm-surface/5 border-white/10',
};
const TYPE_ICONS = { supplement: FlaskConical, medication: Pill, food: Utensils };

// =====================================================================
// PASTE PROTOCOL PARSER
// =====================================================================
const COLUMN_MAP = {
  'time': 'timing', 'timing': 'timing', 'schedule': 'timing', 'when': 'timing',
  'category': 'domain', 'domain': 'domain', 'type': 'domain', 'axis': 'domain',
  'code': 'code', 'item code': 'code', 'id': 'code',
  'name': 'title', 'title': 'title', 'item': 'title', 'item name': 'title',
  'dose': 'dose', 'dosage': 'dose', 'amount': 'dose',
  'instructions': 'instruction', 'instruction': 'instruction', 'notes': 'instruction', 'note': 'instruction',
  'context': 'context', 'location': 'context', 'where': 'context',
  'visibility': 'visibility',
};

const VALID_CATEGORIES = new Set([
  'lifeflow', 'medication', 'food', 'movement', 'supplement', 'diet', 'lifestyle',
  'therapeutic_formulations', 'environment', 'mindflo', 'music_audio',
  'wake', 'morning', 'breakfast', 'lunch', 'afternoon', 'evening', 'dinner', 'night', 'sleep',
]);

function parseProtocolText(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return { items: [], errors: [{ row: 0, message: 'Need at least a header row and one data row' }], delimiter: 'unknown', totalRows: 0, parsedRows: 0, detectedHeaders: [] };

  // Detect delimiter
  const firstLine = lines[0];
  let delimiter = ',';
  if (firstLine.includes('|')) delimiter = '|';
  else if (firstLine.includes('\t')) delimiter = '\t';

  // Parse header
  const rawHeaders = firstLine.split(delimiter).map(h => h.trim());
  const headers = rawHeaders.map(h => h.toLowerCase().replace(/[^a-z\s]/g, '').trim());
  const mappedHeaders = headers.map(h => COLUMN_MAP[h] || null);
  const unmappedHeaders = rawHeaders.filter((_, i) => !mappedHeaders[i]);

  // Check required columns
  const mappedFields = new Set(mappedHeaders.filter(Boolean));
  const missingRequired = [];
  if (!mappedFields.has('title') && !mappedFields.has('code')) missingRequired.push('Name or Code');

  const items = [];
  const errors = [];
  if (missingRequired.length) {
    errors.push({ row: 1, message: `Missing required columns: ${missingRequired.join(', ')}. Found: ${rawHeaders.join(', ')}` });
  }

  const seenCodes = new Set();

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const cells = line.split(delimiter).map(c => c.trim());
    const item = { timing: '', domain: 'therapeutic_formulations', code: '', title: '', dose: '', instruction: '', context: '', visibility: 'both' };
    const rowErrors = [];

    cells.forEach((cell, ci) => {
      const field = mappedHeaders[ci];
      if (field && cell && !['na', 'n/a', '-', ''].includes(cell.toLowerCase())) {
        item[field] = cell;
      }
    });

    // Skip completely empty rows
    if (!item.title && !item.code) continue;

    // Validate time format
    if (item.timing) {
      const t = item.timing;
      const validTime = /^\d{1,2}[:.h]\d{2}/.test(t) || /^\d{1,2}\s*(am|pm)/i.test(t) ||
        ['morning', 'afternoon', 'evening', 'night', 'wake', 'sleep', 'breakfast', 'lunch', 'dinner', 'midday'].includes(t.toLowerCase());
      if (!validTime) rowErrors.push(`Time format unclear: "${t}"`);
    }

    // Validate category
    if (item.domain && !VALID_CATEGORIES.has(item.domain.toLowerCase())) {
      // Try to map common category names
      const cat = item.domain.toLowerCase();
      if (cat.includes('med')) item.domain = 'medication';
      else if (cat.includes('food') || cat.includes('diet') || cat.includes('nutri')) item.domain = 'diet';
      else if (cat.includes('move') || cat.includes('exerc') || cat.includes('physi')) item.domain = 'movement';
      else if (cat.includes('supp') || cat.includes('thera') || cat.includes('formula')) item.domain = 'therapeutic_formulations';
      else if (cat.includes('life') || cat.includes('flow')) item.domain = 'lifestyle';
      else rowErrors.push(`Unknown category: "${item.domain}"`);
    }

    // Check unique codes
    if (item.code) {
      if (seenCodes.has(item.code)) {
        rowErrors.push(`Duplicate code: ${item.code}`);
      } else {
        seenCodes.add(item.code);
      }
    }

    items.push({ ...item, rowNumber: i + 1, errors: rowErrors });
    if (rowErrors.length) errors.push({ row: i + 1, message: rowErrors.join('; ') });
  }

  return {
    items,
    errors,
    delimiter: delimiter === '|' ? 'pipe' : delimiter === '\t' ? 'tab' : 'comma',
    totalRows: lines.length - 1,
    parsedRows: items.length,
    unmappedHeaders,
    detectedHeaders: rawHeaders,
    mappedFields: [...mappedFields],
  };
}


// ===== PASTE PROTOCOL COMPONENT =====
function PasteProtocol({ onParsed, onBack }) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null);

  const handleParse = () => {
    if (!text.trim()) { toast.error('Paste protocol data first'); return; }
    const result = parseProtocolText(text);
    setPreview(result);
    if (result.items.length === 0) {
      toast.error('Could not parse any items from the pasted data');
    } else {
      toast.success(`Parsed ${result.items.length} items (${result.delimiter}-separated)`);
    }
  };

  if (preview) {
    return <PastePreview preview={preview} onConfirm={onParsed} onBack={() => setPreview(null)} />;
  }

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm text-white/70">Paste structured protocol data</Label>
        <p className="text-[11px] text-white/40 mt-1 mb-3">
          Supports comma-separated, pipe-separated (|), or tab-separated rows. Include a header row with column names.
        </p>
        <Textarea
          value={text}
          onChange={e => setText(e.target.value)}
          className="min-h-[220px] font-mono text-xs bg-adm-bg border-white/[0.06] text-white/80 leading-relaxed"
          placeholder={`Time,Category,Code,Name,Dose,Instructions,Context\n06:30,LifeFlow,P1-LF01,Wake,NA,NA,Home\n06:45,Medication,P1-MED01,Levodopa,100mg,Empty stomach,Home\n07:15,Diet,P1-D01,Breakfast,NA,Light protein,Home`}
          data-testid="paste-protocol-textarea"
        />
      </div>

      <Card className="bg-white/[0.02] border-white/[0.06]">
        <CardContent className="p-3">
          <p className="text-[10px] text-white/40 uppercase tracking-wider mb-2">Accepted column names</p>
          <div className="flex flex-wrap gap-1.5">
            {['Time', 'Category', 'Code', 'Name/Title', 'Dose', 'Instructions', 'Context', 'Visibility'].map(c => (
              <Badge key={c} className="text-[9px] bg-adm-surface/5 text-white/50 border-white/10">{c}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-white/40 text-xs">
          <ChevronLeft className="w-3 h-3 mr-1" /> Back
        </Button>
        <Button onClick={handleParse} disabled={!text.trim()} className="bg-[#4E7A6B] hover:bg-[#3D695B] text-white" data-testid="parse-paste-btn">
          <ClipboardPaste className="w-4 h-4 mr-1" /> Parse & Preview
        </Button>
      </div>
    </div>
  );
}


// ===== PASTE PREVIEW WITH VALIDATION =====
function PastePreview({ preview, onConfirm, onBack }) {
  const [items, setItems] = useState(preview.items.map(it => ({ ...it })));
  const [editingIdx, setEditingIdx] = useState(null);
  const [removedIdxs, setRemovedIdxs] = useState(new Set());

  const updateItem = (idx, field, val) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };
  const removeItem = (idx) => setRemovedIdxs(prev => new Set([...prev, idx]));
  const finalItems = items.filter((_, i) => !removedIdxs.has(i));
  const errorItems = finalItems.filter(it => it.errors?.length > 0);

  return (
    <div className="space-y-4" data-testid="paste-preview">
      {/* Summary */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-white/90 flex items-center gap-2">
            <ClipboardPaste className="w-4 h-4 text-[#C89B7B]" /> Parsed — {finalItems.length} Items
          </h3>
          <p className="text-xs text-white/40 mt-0.5">
            Format: {preview.delimiter} | {preview.totalRows} rows found | {preview.parsedRows} parsed
          </p>
        </div>
        <div className="flex gap-2">
          {preview.mappedFields?.map(f => (
            <Badge key={f} className="text-[9px] bg-[#4E7A6B]/10 text-[#4E7A6B] border-[#4E7A6B]/20 capitalize">{f}</Badge>
          ))}
        </div>
      </div>

      {/* Unmapped headers warning */}
      {preview.unmappedHeaders?.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/5 rounded-lg px-3 py-2 border border-amber-500/20">
          <Info className="w-3.5 h-3.5 flex-shrink-0" />
          Unmapped columns (ignored): {preview.unmappedHeaders.join(', ')}
        </div>
      )}

      {/* Validation errors */}
      {preview.errors?.length > 0 && (
        <Card className="dark:bg-red-500/5 dark:border-red-500/20">
          <CardContent className="p-3 space-y-1.5">
            <h4 className="text-xs font-semibold text-red-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Validation Issues ({preview.errors.length})
            </h4>
            {preview.errors.map((err, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <Badge className="text-[9px] bg-red-500/10 text-red-400 border-red-500/20 flex-shrink-0">Row {err.row}</Badge>
                <span className="text-white/60">{err.message}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Items table */}
      <div className="rounded-lg border border-white/[0.06] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/[0.06]">
              <TableHead className="text-[10px] w-8">#</TableHead>
              <TableHead className="text-[10px]">Time</TableHead>
              <TableHead className="text-[10px]">Code</TableHead>
              <TableHead className="text-[10px]">Name</TableHead>
              <TableHead className="text-[10px]">Domain</TableHead>
              <TableHead className="text-[10px]">Dose</TableHead>
              <TableHead className="text-[10px]">Instructions</TableHead>
              <TableHead className="text-[10px] w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, idx) => {
              if (removedIdxs.has(idx)) return null;
              const hasErrors = item.errors?.length > 0;
              const isEditing = editingIdx === idx;

              if (isEditing) {
                return (
                  <TableRow key={idx} className="border-white/[0.06]">
                    <TableCell colSpan={8} className="p-2">
                      <div className="grid grid-cols-6 gap-2" data-testid={`paste-edit-row-${idx}`}>
                        <Input value={item.timing} onChange={e => updateItem(idx, 'timing', e.target.value)} className="h-7 text-xs bg-[#0B0F19] border-white/10" placeholder="Time" />
                        <Input value={item.code} onChange={e => updateItem(idx, 'code', e.target.value)} className="h-7 text-xs bg-[#0B0F19] border-white/10" placeholder="Code" />
                        <Input value={item.title} onChange={e => updateItem(idx, 'title', e.target.value)} className="h-7 text-xs bg-[#0B0F19] border-white/10 col-span-2" placeholder="Name" />
                        <Input value={item.dose} onChange={e => updateItem(idx, 'dose', e.target.value)} className="h-7 text-xs bg-[#0B0F19] border-white/10" placeholder="Dose" />
                        <Button variant="ghost" size="sm" onClick={() => { updateItem(idx, 'errors', []); setEditingIdx(null); }} className="text-[#4E7A6B] h-7 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" /> Done
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <TableRow key={idx} className={`border-white/[0.06] ${hasErrors ? 'bg-red-500/5' : ''}`} data-testid={`paste-row-${idx}`}>
                  <TableCell className="text-[10px] text-white/30">{item.rowNumber}</TableCell>
                  <TableCell className="text-xs text-white/60 font-mono">{item.timing || '—'}</TableCell>
                  <TableCell className="text-xs text-white/50 font-mono">{item.code || '—'}</TableCell>
                  <TableCell className="text-sm text-white/90">{item.title}</TableCell>
                  <TableCell>
                    <Badge className="text-[9px] bg-[#4E7A6B]/10 text-[#4E7A6B] border-[#4E7A6B]/20 capitalize">{item.domain?.replace('_', ' ')}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-white/50">{item.dose || '—'}</TableCell>
                  <TableCell className="text-xs text-white/40 max-w-[200px] truncate">{item.instruction || '—'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditingIdx(idx)}>
                        <Edit3 className="w-3 h-3 text-white/40" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(idx)}>
                        <Trash2 className="w-3 h-3 text-red-400/50" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-2">
        <Button variant="outline" onClick={onBack} className="border-white/10 text-white/50">
          <ChevronLeft className="w-4 h-4 mr-1" /> Re-paste
        </Button>
        <Button onClick={() => onConfirm(finalItems)} disabled={finalItems.length === 0}
          className="bg-[#4E7A6B] hover:bg-[#3D695B] text-white" data-testid="confirm-paste-btn">
          <CheckCircle className="w-4 h-4 mr-1" /> Confirm & Fill Protocol ({finalItems.length} items)
        </Button>
      </div>
    </div>
  );
}


// ===== UPLOAD & EXTRACT COMPONENT =====
function ProtocolUploader({ subscriberId, onExtracted }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
      toast.error('Unsupported file type. Use PDF, PNG, JPG, or WEBP.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large (max 20MB)');
      return;
    }

    setUploading(true);
    setDiagnostics(null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('subscriber_id', subscriberId || '');

    try {
      const r = await axios.post(`${API}/protocol/upload-extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      if (r.data.total_items === 0) {
        setDiagnostics(r.data.diagnostics || { reason: 'No items extracted' });
        toast.error('Extraction found no protocol items');
      } else {
        toast.success(`Extracted ${r.data.total_items} items from ${file.name}`);
        onExtracted(r.data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.diagnostics) {
        setDiagnostics(detail.diagnostics);
        toast.error(detail.detail || 'Extraction failed');
      } else {
        toast.error(detail || 'Extraction failed. Try a clearer document or use Paste Protocol.');
      }
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="space-y-3">
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
          ${dragOver ? 'border-[#4E7A6B] bg-[#4E7A6B]/5' : 'border-white/10 hover:border-white/20'}
          ${uploading ? 'pointer-events-none opacity-60' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && fileRef.current?.click()}
        data-testid="protocol-upload-zone"
      >
        <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={e => handleFile(e.target.files?.[0])} />
        {uploading ? (
          <div className="space-y-3">
            <Loader2 className="w-10 h-10 text-[#4E7A6B] animate-spin mx-auto" />
            <p className="text-white/70 font-medium">Analyzing protocol sheet...</p>
            <p className="text-white/40 text-xs">Extracting items, medications, food blocks & timing</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Upload className="w-10 h-10 text-white/30 mx-auto" />
            <div>
              <p className="text-white/70 font-medium">Drop protocol sheet here</p>
              <p className="text-white/40 text-xs mt-1">PDF, PNG, JPG, WEBP (max 20MB)</p>
            </div>
          </div>
        )}
      </div>

      {/* Extraction Diagnostics */}
      {diagnostics && (
        <Card className="dark:bg-amber-500/5 dark:border-amber-500/20" data-testid="extraction-diagnostics">
          <CardContent className="p-3 space-y-2">
            <h4 className="text-xs font-semibold text-amber-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Extraction Diagnostics
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
              <div className="bg-adm-bg rounded-lg p-2">
                <p className="text-white/30">Table detected</p>
                <p className="text-white/70 font-semibold">{diagnostics.table_detected ? 'Yes' : 'No'}</p>
              </div>
              <div className="bg-adm-bg rounded-lg p-2">
                <p className="text-white/30">Table rows</p>
                <p className="text-white/70 font-semibold">{diagnostics.table_rows_found ?? diagnostics.rows_extracted ?? '0'}</p>
              </div>
              <div className="bg-adm-bg rounded-lg p-2">
                <p className="text-white/30">Text lines</p>
                <p className="text-white/70 font-semibold">{diagnostics.total_text_lines ?? '0'}</p>
              </div>
              <div className="bg-adm-bg rounded-lg p-2">
                <p className="text-white/30">Items parsed</p>
                <p className="text-white/70 font-semibold">{diagnostics.items_parsed ?? '0'}</p>
              </div>
            </div>
            {diagnostics.reason && (
              <p className="text-[11px] text-amber-400/80 leading-relaxed">{diagnostics.reason}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ===== EXTRACTION PREVIEW (unchanged) =====
function ExtractionPreview({ extraction, onConfirm, onDiscard }) {
  const [editingIdx, setEditingIdx] = useState(null);
  const [items, setItems] = useState(extraction.extracted_items || []);
  const [removedIdxs, setRemovedIdxs] = useState(new Set());
  const updateItem = (idx, field, val) => setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  const removeItem = (idx) => setRemovedIdxs(prev => new Set([...prev, idx]));
  const finalItems = items.filter((_, i) => !removedIdxs.has(i));
  const typeCounts = {
    supplements: finalItems.filter(i => i.type_tag === 'supplement').length,
    medications: finalItems.filter(i => i.type_tag === 'medication').length,
    food: finalItems.filter(i => i.type_tag === 'food').length,
    other: finalItems.filter(i => !['supplement', 'medication', 'food'].includes(i.type_tag)).length,
  };

  return (
    <div className="space-y-4" data-testid="extraction-preview">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-white/90 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C89B7B]" /> Extraction Complete — {finalItems.length} Items
          </h3>
          <p className="text-xs text-white/40 mt-0.5">{extraction.filename} | {extraction.summary}</p>
        </div>
        <div className="flex gap-2">
          {Object.entries(typeCounts).filter(([, c]) => c > 0).map(([type, count]) => (
            <Badge key={type} className="text-[10px] bg-adm-surface/5 text-white/60 border-white/10 capitalize">{type}: {count}</Badge>
          ))}
        </div>
      </div>
      {extraction.critical_instructions?.length > 0 && (
        <Card className="dark:bg-amber-500/5 dark:border-amber-500/20">
          <CardContent className="p-3">
            <h4 className="text-xs font-semibold text-amber-400 flex items-center gap-1.5 mb-2"><AlertTriangle className="w-3.5 h-3.5" /> Critical Instructions</h4>
            {extraction.critical_instructions.map((ci, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <Badge className="text-[9px] bg-amber-500/10 text-amber-400 border-amber-500/20 flex-shrink-0 capitalize">{ci.type?.replace('_', ' ')}</Badge>
                <span className="text-white/60">{ci.rule}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {Object.entries(extraction.grouped_by_block || {}).map(([block, blockItems]) => {
        const BlockIcon = BLOCK_ICONS[block] || Clock;
        const colors = BLOCK_COLORS[block] || BLOCK_COLORS.anytime;
        return (
          <Card key={block} className={`bg-adm-surface border ${colors.split(' ')[2]}`}>
            <CardHeader className="pb-1 pt-3 px-4">
              <div className="flex items-center gap-2">
                <BlockIcon className={`w-4 h-4 ${colors.split(' ')[0]}`} />
                <span className="text-xs font-semibold uppercase tracking-wider text-white/60 capitalize">{block}</span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {blockItems.map((item) => {
                const globalIdx = items.findIndex(i => i.code === item.code && i.title === item.title);
                if (removedIdxs.has(globalIdx)) return null;
                const isEditing = editingIdx === globalIdx;
                const TypeIcon = TYPE_ICONS[item.type_tag] || FlaskConical;
                return (
                  <div key={globalIdx} className="border-t border-white/5 px-4 py-2.5">
                    {isEditing ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <Input value={items[globalIdx].code} onChange={e => updateItem(globalIdx, 'code', e.target.value)} className="h-7 text-xs bg-[#0B0F19] border-white/10" placeholder="Code" />
                          <Input value={items[globalIdx].title} onChange={e => updateItem(globalIdx, 'title', e.target.value)} className="h-7 text-xs bg-[#0B0F19] border-white/10 col-span-2" placeholder="Title" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Input value={items[globalIdx].timing} onChange={e => updateItem(globalIdx, 'timing', e.target.value)} className="h-7 text-xs bg-[#0B0F19] border-white/10" placeholder="Timing" />
                          <Input value={items[globalIdx].dose} onChange={e => updateItem(globalIdx, 'dose', e.target.value)} className="h-7 text-xs bg-[#0B0F19] border-white/10" placeholder="Dose" />
                          <Select value={items[globalIdx].domain} onValueChange={v => updateItem(globalIdx, 'domain', v)}>
                            <SelectTrigger className="h-7 text-xs bg-[#0B0F19] border-white/10"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#131B2B] border-white/10">{DOMAINS.map(d => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setEditingIdx(null)} className="text-[#4E7A6B] h-6 text-xs"><CheckCircle className="w-3 h-3 mr-1" /> Done</Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <TypeIcon className={`w-4 h-4 flex-shrink-0 ${item.type_tag === 'medication' ? 'text-blue-400' : item.type_tag === 'food' ? 'text-amber-400' : 'text-[#4E7A6B]'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-white/40">{item.code}</span>
                            <span className="text-sm text-white/90 font-medium truncate">{item.title}</span>
                            {item.dose && <Badge className="text-[9px] bg-adm-surface/5 text-white/50 border-0">{item.dose}</Badge>}
                          </div>
                          {item.instruction && <p className="text-[10px] text-white/40 mt-0.5 truncate">{item.instruction}</p>}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0"><Clock className="w-3 h-3 text-white/30" /><span className="text-xs text-white/50">{item.timing}</span></div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditingIdx(globalIdx)}><Edit3 className="w-3 h-3 text-white/40" /></Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(globalIdx)}><Trash2 className="w-3 h-3 text-red-400/50" /></Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
      <div className="flex justify-between items-center pt-2">
        <Button variant="outline" onClick={onDiscard} className="border-white/10 text-white/50"><X className="w-4 h-4 mr-1" /> Discard</Button>
        <Button onClick={() => onConfirm(finalItems)} className="bg-[#4E7A6B] hover:bg-[#3D695B] text-white" data-testid="confirm-extraction-btn">
          <CheckCircle className="w-4 h-4 mr-1" /> Confirm & Fill Protocol ({finalItems.length} items)
        </Button>
      </div>
    </div>
  );
}


// ===== COLUMN REMAPPER (3-pane: Raw Rows | Mapping | Preview) =====
const REMAP_FIELDS = [
  { id: 'skip', label: 'Skip' },
  { id: 'time', label: 'Time' },
  { id: 'category', label: 'Category' },
  { id: 'code', label: 'Code' },
  { id: 'name', label: 'Name' },
  { id: 'dose', label: 'Dose' },
  { id: 'instruction', label: 'Instructions' },
  { id: 'context', label: 'Context' },
];

const TYPE_BADGE = {
  medication: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  food: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  regenflo: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  life_event: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  other: 'bg-white/5 text-white/40 border-white/10',
};

function ColumnRemapper({ rawRows, rawHeaders, onComplete, onBack }) {
  const [mapping, setMapping] = useState({});
  const [editedRows, setEditedRows] = useState(() => rawRows.map(r => [...r]));
  const [deletedRows, setDeletedRows] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [addingRow, setAddingRow] = useState(false);
  const [newRow, setNewRow] = useState([]);

  const colCount = rawRows[0]?.length || 0;

  // Auto-detect mapping from headers
  useEffect(() => {
    if (rawHeaders && rawHeaders.length > 0) {
      const autoMap = {};
      rawHeaders.forEach((h, i) => {
        const lc = h.toLowerCase().trim();
        if (['time', 'timing', 'schedule', 'when'].includes(lc)) autoMap[i] = 'time';
        else if (['category', 'type', 'axis', 'domain'].includes(lc)) autoMap[i] = 'category';
        else if (['code', 'item code', 'id', 'sr'].includes(lc)) autoMap[i] = 'code';
        else if (['name', 'title', 'item', 'item name'].includes(lc)) autoMap[i] = 'name';
        else if (['dose', 'dosage', 'amount'].includes(lc)) autoMap[i] = 'dose';
        else if (['instructions', 'instruction', 'notes', 'note'].includes(lc)) autoMap[i] = 'instruction';
        else if (['context', 'location', 'where'].includes(lc)) autoMap[i] = 'context';
      });
      setMapping(autoMap);
    }
  }, [rawHeaders]);

  const updateMapping = (colIdx, field) => {
    setMapping(prev => ({ ...prev, [colIdx]: field }));
    setPreview(null);
  };

  const activeRows = editedRows.filter((_, i) => !deletedRows.has(i));
  const mappedFields = new Set(Object.values(mapping).filter(v => v !== 'skip'));
  const hasTime = mappedFields.has('time');
  const hasName = mappedFields.has('name');
  const isValid = hasTime && hasName;

  const handlePreview = async () => {
    if (!isValid) {
      toast.error('Map at least Time and Name columns');
      return;
    }
    setLoading(true);
    try {
      const edits = [];
      deletedRows.forEach(idx => edits.push({ action: 'delete', row_index: idx }));
      editedRows.forEach((row, idx) => {
        if (!deletedRows.has(idx) && JSON.stringify(row) !== JSON.stringify(rawRows[idx])) {
          edits.push({ action: 'edit', row_index: idx, cells: row });
        }
      });

      const resp = await axios.post(`${API}/executable-protocol/remap`, {
        raw_rows: rawRows,
        column_mapping: mapping,
        edits,
      });
      setPreview(resp.data);
      toast.success(`Mapped ${resp.data.total_items} items`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remap failed');
    }
    setLoading(false);
  };

  const startEditCell = (rowIdx, colIdx) => {
    setEditingCell({ row: rowIdx, col: colIdx });
    setEditValue(editedRows[rowIdx]?.[colIdx] || '');
  };

  const saveEditCell = () => {
    if (!editingCell) return;
    setEditedRows(prev => {
      const next = prev.map(r => [...r]);
      if (next[editingCell.row]) {
        next[editingCell.row][editingCell.col] = editValue;
      }
      return next;
    });
    setEditingCell(null);
    setPreview(null);
  };

  const deleteRow = (idx) => {
    setDeletedRows(prev => new Set([...prev, idx]));
    setPreview(null);
  };

  const restoreRow = (idx) => {
    setDeletedRows(prev => { const next = new Set(prev); next.delete(idx); return next; });
    setPreview(null);
  };

  const startAddRow = () => {
    setNewRow(Array(colCount).fill(''));
    setAddingRow(true);
  };

  const confirmAddRow = () => {
    setEditedRows(prev => [...prev, newRow]);
    setAddingRow(false);
    setPreview(null);
  };

  const handleConfirmImport = () => {
    if (!preview || !preview.items?.length) {
      toast.error('No items to import');
      return;
    }
    onComplete({
      ...preview,
      remap_audit: {
        ...(preview.remap_audit || {}),
        original_raw_rows: rawRows,
      },
    });
  };

  return (
    <div className="space-y-4" data-testid="column-remapper">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-white/90 flex items-center gap-2">
            <Columns className="w-4 h-4 text-[#C89B7B]" /> Column Remapper
          </h3>
          <p className="text-xs text-white/40 mt-0.5">
            {rawRows.length} raw rows detected, {colCount} columns
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onBack} className="border-white/10 text-white/50 text-xs">
            <ChevronLeft className="w-3 h-3 mr-1" /> Back
          </Button>
        </div>
      </div>

      {/* PANE 1: Column Mapping Controls */}
      <Card className="bg-adm-surface border-white/[0.06]" data-testid="remap-mapping-pane">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-xs uppercase tracking-wider text-white/50">Column Mapping</CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {Array.from({ length: colCount }, (_, i) => (
              <div key={i} className="space-y-1">
                <Label className="text-[10px] text-white/30">
                  Col {i + 1} {rawHeaders?.[i] ? `(${rawHeaders[i]})` : ''}
                </Label>
                <Select value={mapping[i] || 'skip'} onValueChange={(v) => updateMapping(i, v)}>
                  <SelectTrigger className="h-7 text-xs bg-adm-bg border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-adm-surface border-white/10">
                    {REMAP_FIELDS.map(f => (
                      <SelectItem key={f.id} value={f.id} className="text-xs">{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-3">
            <div className="flex gap-2 text-[10px]">
              <Badge className={hasTime ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                Time {hasTime ? '✓' : '✗'}
              </Badge>
              <Badge className={hasName ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                Name {hasName ? '✓' : '✗'}
              </Badge>
              <Badge className={mappedFields.has('category') ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}>
                Category {mappedFields.has('category') ? '✓' : '~'}
              </Badge>
              <Badge className={mappedFields.has('code') ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}>
                Code {mappedFields.has('code') ? '✓' : '~'}
              </Badge>
            </div>
            {!mappedFields.has('category') && isValid && (
              <span className="text-[10px] text-amber-400/70">Category missing — fallback classification will be used</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* PANE 2: Raw Rows (editable) */}
      <Card className="bg-adm-surface border-white/[0.06]" data-testid="remap-raw-pane">
        <CardHeader className="pb-2 pt-3 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xs uppercase tracking-wider text-white/50">
              Raw Extracted Rows ({activeRows.length} active)
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={startAddRow} className="text-[#4E7A6B] text-xs h-6">
              <Plus className="w-3 h-3 mr-1" /> Add Row
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5">
                <TableHead className="text-[10px] text-white/30 w-8">#</TableHead>
                {Array.from({ length: colCount }, (_, i) => (
                  <TableHead key={i} className="text-[10px] text-white/30 min-w-[80px]">
                    {mapping[i] && mapping[i] !== 'skip' ? (
                      <Badge className="text-[9px] bg-[#4E7A6B]/10 text-[#4E7A6B] border-[#4E7A6B]/20">{mapping[i]}</Badge>
                    ) : (
                      <span className="text-white/20">Col {i + 1}</span>
                    )}
                  </TableHead>
                ))}
                <TableHead className="text-[10px] text-white/30 w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {editedRows.map((row, rowIdx) => {
                const isDeleted = deletedRows.has(rowIdx);
                return (
                  <TableRow key={rowIdx} className={`border-white/5 ${isDeleted ? 'opacity-30 line-through' : ''}`}>
                    <TableCell className="text-[10px] text-white/20 py-1.5">{rowIdx + 1}</TableCell>
                    {row.map((cell, colIdx) => {
                      const isEditing = editingCell?.row === rowIdx && editingCell?.col === colIdx;
                      return (
                        <TableCell key={colIdx} className="py-1 px-2">
                          {isEditing ? (
                            <Input
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onBlur={saveEditCell}
                              onKeyDown={e => e.key === 'Enter' && saveEditCell()}
                              autoFocus
                              className="h-6 text-[11px] bg-adm-bg border-[#4E7A6B]/40"
                            />
                          ) : (
                            <span
                              className="text-[11px] text-white/70 cursor-pointer hover:text-white transition-colors block truncate max-w-[120px]"
                              onClick={() => !isDeleted && startEditCell(rowIdx, colIdx)}
                              title={cell || '(empty)'}
                            >
                              {cell || <span className="text-white/20">—</span>}
                            </span>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell className="py-1">
                      {isDeleted ? (
                        <Button size="sm" variant="ghost" onClick={() => restoreRow(rowIdx)} className="h-5 w-5 p-0 text-emerald-400 hover:text-emerald-300">
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => deleteRow(rowIdx)} className="h-5 w-5 p-0 text-red-400/50 hover:text-red-400">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {addingRow && (
                <TableRow className="border-white/5 bg-[#4E7A6B]/5">
                  <TableCell className="text-[10px] text-[#4E7A6B] py-1.5">+</TableCell>
                  {newRow.map((cell, colIdx) => (
                    <TableCell key={colIdx} className="py-1 px-2">
                      <Input
                        value={cell}
                        onChange={e => setNewRow(prev => { const n = [...prev]; n[colIdx] = e.target.value; return n; })}
                        placeholder={mapping[colIdx] || `Col ${colIdx + 1}`}
                        className="h-6 text-[11px] bg-adm-bg border-white/10"
                      />
                    </TableCell>
                  ))}
                  <TableCell className="py-1 flex gap-1">
                    <Button size="sm" variant="ghost" onClick={confirmAddRow} className="h-5 w-5 p-0 text-emerald-400"><CheckCircle className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setAddingRow(false)} className="h-5 w-5 p-0 text-white/30"><X className="w-3 h-3" /></Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Apply Mapping Button */}
      <div className="flex justify-center">
        <Button
          onClick={handlePreview}
          disabled={!isValid || loading}
          className="bg-[#C89B7B] hover:bg-[#B88A6A] text-white px-8"
          data-testid="remap-preview-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
          Apply Mapping & Preview
        </Button>
      </div>

      {/* PANE 3: Normalized Preview */}
      {preview && (
        <Card className="bg-adm-surface border-[#4E7A6B]/20" data-testid="remap-preview-pane">
          <CardHeader className="pb-2 pt-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs uppercase tracking-wider text-[#4E7A6B]">
                Normalized Preview — {preview.total_items} items
              </CardTitle>
              <div className="flex gap-2 text-[10px]">
                {Object.entries(preview.type_counts || {}).map(([type, count]) => (
                  <Badge key={type} className={TYPE_BADGE[type] || TYPE_BADGE.other}>
                    {type}: {count}
                  </Badge>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Validation Warnings */}
            {preview.validation_warnings?.length > 0 && (
              <div className="px-4 py-2 border-b border-white/5 space-y-1">
                {preview.validation_warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px]">
                    <Badge className={`text-[9px] flex-shrink-0 ${w.type === 'error' ? 'bg-red-500/10 text-red-400 border-red-500/20' : w.type === 'info' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}>
                      {w.type}
                    </Badge>
                    <span className="text-white/60">{w.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Items Table */}
            <Table>
              <TableHeader>
                <TableRow className="border-white/5">
                  <TableHead className="text-[10px] text-white/30 w-16">Time</TableHead>
                  <TableHead className="text-[10px] text-white/30 w-20">Type</TableHead>
                  <TableHead className="text-[10px] text-white/30 w-20">Code</TableHead>
                  <TableHead className="text-[10px] text-white/30">Name</TableHead>
                  <TableHead className="text-[10px] text-white/30 w-24">Dose</TableHead>
                  <TableHead className="text-[10px] text-white/30">Instructions</TableHead>
                  <TableHead className="text-[10px] text-white/30 w-16">Context</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.items?.map((item, i) => (
                  <TableRow key={i} className="border-white/5">
                    <TableCell className="text-xs font-mono text-white/60 py-1.5">{item.timing_text}</TableCell>
                    <TableCell className="py-1.5">
                      <Badge className={`text-[9px] ${TYPE_BADGE[item.item_type] || TYPE_BADGE.other}`}>{item.item_type}</Badge>
                    </TableCell>
                    <TableCell className="text-[11px] text-white/50 font-mono py-1.5">{item.code}</TableCell>
                    <TableCell className="text-xs text-white/90 py-1.5">{item.title}</TableCell>
                    <TableCell className="text-[11px] text-white/50 py-1.5">{item.dose_text}</TableCell>
                    <TableCell className="text-[11px] text-white/50 py-1.5 max-w-[200px] truncate">{item.instruction_text}</TableCell>
                    <TableCell className="text-[11px] text-white/40 py-1.5">{item.location_context}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Confirm Import */}
      {preview?.items?.length > 0 && (
        <div className="flex justify-between items-center pt-2">
          <Button variant="outline" onClick={() => setPreview(null)} className="border-white/10 text-white/50">
            <RotateCcw className="w-4 h-4 mr-1" /> Re-map
          </Button>
          <Button
            onClick={handleConfirmImport}
            className="bg-[#4E7A6B] hover:bg-[#3D695B] text-white px-8"
            data-testid="remap-confirm-import-btn"
          >
            <Save className="w-4 h-4 mr-1" /> Confirm & Import ({preview.total_items} items)
          </Button>
        </div>
      )}
    </div>
  );
}


// ===== EXECUTABLE PREVIEW (unchanged) =====
function ExecutablePreview({ extraction, onSave, saving, onDiscard, onRemap }) {
  const BLOCK_ORDER = ['wake', 'morning', 'mid_morning', 'breakfast', 'lunch', 'afternoon', 'evening', 'dinner', 'night', 'sleep', 'anytime'];
  const BLOCK_LABELS = { wake: 'Wake', morning: 'Morning', mid_morning: 'Mid-Morning', breakfast: 'Breakfast', lunch: 'Lunch', afternoon: 'Afternoon', evening: 'Evening', dinner: 'Dinner', night: 'Night', sleep: 'Sleep', anytime: 'Anytime' };
  const TYPE_ICONS_P = { regenflo: FlaskConical, medication: Pill, food: Utensils, life_event: Clock, routine: Clock, other: Clock };
  const TYPE_COLORS_P = { regenflo: 'text-[#4E7A6B]', medication: 'text-blue-400', food: 'text-amber-400', life_event: 'text-purple-400', other: 'text-white/40' };
  const CONF_COLORS = { high: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20', low: 'bg-red-500/10 text-red-400 border-red-500/20' };
  const getConfLevel = (c) => c >= 0.8 ? 'high' : c >= 0.5 ? 'medium' : 'low';
  const sortedBlocks = BLOCK_ORDER.filter(b => extraction.events_by_block?.[b]);

  // Show diagnostics if extraction had issues
  const diag = extraction.diagnostics;

  return (
    <div className="space-y-4" data-testid="exec-preview">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-white/90 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C89B7B]" /> Executable Protocol Preview
          </h3>
          <p className="text-xs text-white/40 mt-0.5">
            {extraction.total_items} items, {extraction.total_events} events, {extraction.rule_groups?.length || 0} rules
          </p>
        </div>
      </div>

      {/* Diagnostics if partial/failed extraction */}
      {diag && (
        <Card className="dark:bg-white/[0.02] dark:border-white/[0.06]" data-testid="exec-diagnostics">
          <CardContent className="p-3">
            <div className="grid grid-cols-4 gap-2 text-[10px]">
              <div className="bg-adm-bg rounded-lg p-2"><p className="text-white/30">Table</p><p className="text-white/70 font-semibold">{diag.table_detected ? 'Yes' : 'No'}</p></div>
              <div className="bg-adm-bg rounded-lg p-2"><p className="text-white/30">Rows</p><p className="text-white/70 font-semibold">{diag.table_rows_found}</p></div>
              <div className="bg-adm-bg rounded-lg p-2"><p className="text-white/30">Items</p><p className="text-white/70 font-semibold">{diag.items_parsed}</p></div>
              <div className="bg-adm-bg rounded-lg p-2"><p className="text-white/30">Events</p><p className="text-white/70 font-semibold">{diag.events_generated}</p></div>
            </div>
            {diag.reason && <p className="text-[11px] text-amber-400/80 mt-2">{diag.reason}</p>}
          </CardContent>
        </Card>
      )}

      {extraction.validation_warnings?.length > 0 && (
        <Card className="dark:bg-red-500/5 dark:border-red-500/20">
          <CardContent className="p-3 space-y-1.5">
            <h4 className="text-xs font-semibold text-red-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Validation ({extraction.validation_warnings.length})</h4>
            {extraction.validation_warnings.map((w, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <Badge className={`text-[9px] flex-shrink-0 ${w.type === 'error' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}>{w.type}</Badge>
                <span className="text-white/60">{w.message}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {sortedBlocks.map(block => (
        <Card key={block} className="bg-adm-surface border-white/[0.06]" data-testid={`exec-block-${block}`}>
          <CardHeader className="pb-1 pt-3 px-4"><span className="text-xs font-semibold uppercase tracking-wider text-white/50">{BLOCK_LABELS[block]}</span></CardHeader>
          <CardContent className="p-0">
            {extraction.events_by_block[block].map((evt, i) => {
              const TypeIcon = TYPE_ICONS_P[evt.item_type] || Clock;
              const typeColor = TYPE_COLORS_P[evt.item_type] || 'text-white/40';
              const confLevel = getConfLevel(evt.source_confidence || 0.5);
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-t border-white/5">
                  <span className="text-xs font-mono text-white/30 w-14 flex-shrink-0">{evt.exact_time}</span>
                  <TypeIcon className={`w-4 h-4 ${typeColor} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-white/90">{evt.title}</span>
                    {evt.subtitle && <p className="text-[10px] text-white/40 truncate">{evt.subtitle}</p>}
                  </div>
                  <Badge className={`text-[9px] ${CONF_COLORS[confLevel]}`}>{confLevel}</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {extraction.total_events === 0 && (
        <Card className="dark:bg-adm-surface/[0.02] border-white/[0.06]">
          <CardContent className="py-8 text-center">
            <p className="text-white/40 text-sm">No executable events extracted. Try Paste Protocol for manual structured input.</p>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between items-center pt-2">
        <div className="flex gap-2">
          <Button variant="outline" onClick={onDiscard} className="border-white/10 text-white/50"><X className="w-4 h-4 mr-1" /> Discard</Button>
          {extraction.raw_rows?.length > 0 && onRemap && (
            <Button variant="outline" onClick={onRemap} className="border-[#C89B7B]/30 text-[#C89B7B] hover:bg-[#C89B7B]/10" data-testid="exec-remap-btn">
              <Columns className="w-4 h-4 mr-1" /> Remap Columns
            </Button>
          )}
        </div>
        <Button onClick={onSave} disabled={saving || extraction.total_items === 0} className="bg-[#4E7A6B] hover:bg-[#3D695B] text-white" data-testid="exec-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save & Activate Executable Protocol
        </Button>
      </div>
    </div>
  );
}


// ===== PUBLISH CONFIRMATION DIALOG =====
function PublishConfirmation({ form, selectedSub, onConfirm, onCancel, publishing }) {
  const validItems = form.items.filter(i => i.title.trim());
  const validGoals = form.goals.filter(g => g.trim());
  return (
    <div className="space-y-4 p-5 rounded-xl border-2 border-[#4E7A6B]/30 bg-[#4E7A6B]/5" data-testid="publish-confirmation">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-[#4E7A6B]" />
        <div>
          <h3 className="text-sm font-semibold text-white/90">Confirm Protocol Publish</h3>
          <p className="text-[11px] text-white/40">This will go live immediately</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-adm-bg rounded-lg p-3 border border-white/[0.06]">
          <p className="text-[10px] text-white/30 uppercase">Subscriber</p>
          <p className="text-sm text-white/80 font-medium truncate">{selectedSub?.name || '—'}</p>
        </div>
        <div className="bg-adm-bg rounded-lg p-3 border border-white/[0.06]">
          <p className="text-[10px] text-white/30 uppercase">Phase</p>
          <p className="text-sm text-white/80 font-medium">Phase {form.phase}</p>
        </div>
        <div className="bg-adm-bg rounded-lg p-3 border border-white/[0.06]">
          <p className="text-[10px] text-white/30 uppercase">Items</p>
          <p className="text-sm text-white/80 font-medium">{validItems.length}</p>
        </div>
        <div className="bg-adm-bg rounded-lg p-3 border border-white/[0.06]">
          <p className="text-[10px] text-white/30 uppercase">Start Date</p>
          <p className="text-sm text-white/80 font-medium">{form.start_date}</p>
        </div>
      </div>

      <Card className="dark:bg-amber-500/5 dark:border-amber-500/20">
        <CardContent className="p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-400/80">
            <p className="font-medium mb-1">This will:</p>
            <ul className="space-y-0.5 text-white/50">
              <li>Archive any currently active protocol for this subscriber</li>
              <li>Publish {validItems.length} items as the new active Phase {form.phase}</li>
              <li>Sync to Executable Protocol Review immediately</li>
              <li>Send notification email to subscriber</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="outline" onClick={onCancel} className="border-white/10 text-white/50">Cancel</Button>
        <Button onClick={onConfirm} disabled={publishing} className="bg-[#4E7A6B] hover:bg-[#3D695B] text-white px-8" data-testid="confirm-publish-btn">
          {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
          {publishing ? 'Publishing...' : 'Confirm & Publish'}
        </Button>
      </div>
    </div>
  );
}


// ===== MAIN WIZARD =====
export default function ProtocolWizard() {
  const [step, setStep] = useState(0);
  const [subscribers, setSubscribers] = useState([]);
  const [existingProtocols, setExistingProtocols] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [extraction, setExtraction] = useState(null);
  const [execExtraction, setExecExtraction] = useState(null);
  const [sourceMode, setSourceMode] = useState(null); // 'upload' | 'manual' | 'executable' | 'paste'
  const [remapMode, setRemapMode] = useState(false);
  const [savingExec, setSavingExec] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [form, setForm] = useState({
    subscriber_id: '',
    phase: 1,
    goals: [''],
    items: [{ ...emptyItem }],
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    is_active: true,
  });

  useEffect(() => {
    axios.get(`${API}/users?role=subscriber`).then(r => setSubscribers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.subscriber_id) {
      axios.get(`${API}/protocols/${form.subscriber_id}/history`).then(r => setExistingProtocols(r.data)).catch(() => setExistingProtocols([]));
    }
  }, [form.subscriber_id]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const addGoal = () => setForm(prev => ({ ...prev, goals: [...prev.goals, ''] }));
  const updateGoal = (idx, val) => setForm(prev => ({ ...prev, goals: prev.goals.map((g, i) => i === idx ? val : g) }));
  const removeGoal = (idx) => setForm(prev => ({ ...prev, goals: prev.goals.filter((_, i) => i !== idx) }));
  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, { ...emptyItem, order: prev.items.length }] }));
  const updateItem = (idx, field, val) => setForm(prev => ({ ...prev, items: prev.items.map((item, i) => i === idx ? { ...item, [field]: val } : item) }));
  const removeItem = (idx) => setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

  const handleExtracted = (data) => {
    setExtraction(data);
    if (data.detected_phase) updateField('phase', data.detected_phase);
  };

  const handleExecUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('subscriber_id', form.subscriber_id || '');
    setSavingExec(true);
    try {
      const r = await axios.post(`${API}/executable-protocol/upload-extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000
      });
      setExecExtraction(r.data);
      if (r.data.protocol_meta?.phase_number) updateField('phase', r.data.protocol_meta.phase_number);
      toast.success(`Extracted ${r.data.total_items} items, ${r.data.total_events} schedule events`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.diagnostics) {
        toast.error(detail.detail || 'Extraction failed — check diagnostics');
      } else {
        toast.error(detail || 'Extraction failed');
      }
    }
    setSavingExec(false);
  };

  const handleSaveExecutable = async () => {
    if (!form.subscriber_id) { toast.error('Select a subscriber first'); return; }
    if (!execExtraction) return;
    setSavingExec(true);
    try {
      await axios.post(`${API}/executable-protocol/save`, {
        subscriber_id: form.subscriber_id,
        phase_name: `Phase ${form.phase}`,
        phase_number: form.phase,
        items: execExtraction.items,
        schedule_events: execExtraction.schedule_events,
        rule_groups: execExtraction.rule_groups,
        watchpoints: execExtraction.watchpoints || [],
        source_type: execExtraction.remap_audit ? 'remapped_upload' : 'uploaded_schedule',
        start_date: form.start_date,
        remap_audit: execExtraction.remap_audit || null,
      });
      toast.success('Executable protocol saved & activated');
      setPublished(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSavingExec(false);
  };

  const handleRemapComplete = (remappedData) => {
    setExecExtraction(remappedData);
    setRemapMode(false);
    toast.success(`Remapped: ${remappedData.total_items} items ready for activation`);
  };

  const handleConfirmExtraction = (extractedItems) => {
    const wizardItems = extractedItems.map((item, i) => ({
      code: item.code || '',
      title: item.title || '',
      domain: item.domain || 'therapeutic_formulations',
      timing: item.timing || '',
      instruction: [item.instruction, item.dose ? `Dose: ${item.dose}` : ''].filter(Boolean).join(' | '),
      status: 'active',
      visibility: 'both',
      linked_timeline_event: '',
      order: i,
    }));
    setForm(prev => ({ ...prev, items: wizardItems }));
    setExtraction(null);
    setStep(2);
    toast.success(`${wizardItems.length} items loaded into protocol`);
  };

  const handlePasteConfirm = (pastedItems) => {
    const wizardItems = pastedItems.map((item, i) => ({
      code: item.code || '',
      title: item.title || '',
      domain: item.domain || 'therapeutic_formulations',
      timing: item.timing || '',
      instruction: [item.instruction, item.dose ? `Dose: ${item.dose}` : '', item.context ? `Context: ${item.context}` : ''].filter(Boolean).join(' | '),
      status: 'active',
      visibility: item.visibility || 'both',
      linked_timeline_event: '',
      order: i,
    }));
    setForm(prev => ({ ...prev, items: wizardItems }));
    setSourceMode(null);
    setStep(2);
    toast.success(`${wizardItems.length} items loaded from paste`);
  };

  const handlePublish = async () => {
    const cleanForm = {
      ...form,
      goals: form.goals.filter(g => g.trim()),
      items: form.items.filter(item => item.title.trim()).map((item, i) => ({ ...item, order: i })),
    };
    if (!cleanForm.subscriber_id) { toast.error('Select a subscriber'); return; }
    if (!cleanForm.items.length) { toast.error('Add at least one protocol item'); return; }

    setPublishing(true);
    try {
      await axios.post(`${API}/protocols`, cleanForm);
      toast.success('Protocol published successfully');
      setPublished(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish protocol');
    } finally {
      setPublishing(false);
      setShowConfirm(false);
    }
  };

  const handleArchive = async (protoId) => {
    try {
      await axios.patch(`${API}/protocols/${protoId}/archive`);
      toast.success('Protocol archived');
      setExistingProtocols(prev => prev.map(p => p.id === protoId ? { ...p, status: 'archived' } : p));
    } catch {
      toast.error('Failed to archive protocol');
    }
  };

  const selectedSub = subscribers.find(s => s.id === form.subscriber_id);

  if (published) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#4E7A6B]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#4E7A6B]" />
            </div>
            <h2 className="text-2xl font-['Outfit'] font-bold text-adm-primary mb-2" data-testid="protocol-published-title">Protocol Published</h2>
            <p className="text-adm-primary/60 mb-6">Phase {form.phase} protocol for {selectedSub?.name || 'subscriber'} is now active.</p>
            <Button onClick={() => { setForm({ subscriber_id: '', phase: 1, items: [{ ...emptyItem }], goals: [''], start_date: new Date().toISOString().split('T')[0], end_date: '', is_active: true }); setStep(0); setPublished(false); setSourceMode(null); setExtraction(null); setExecExtraction(null); setShowConfirm(false); }} variant="outline">
              Create Another Protocol
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-bold text-adm-primary flex items-center gap-3" data-testid="protocol-wizard-title">
            <Layers className="w-7 h-7 text-[#4E7A6B]" /> Protocol Creation Wizard
          </h1>
          <p className="text-adm-primary/60 mt-1">Build structured protocols — upload, paste, or create manually</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 overflow-x-auto">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <button onClick={() => setStep(i)}
                className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  i === step ? 'bg-[#4E7A6B]/20 text-[#4E7A6B] border border-[#4E7A6B]/30' :
                  i < step ? 'bg-[#4E7A6B]/10 text-[#4E7A6B]' : 'text-adm-primary/40'}`}
                data-testid={`proto-step-${s.id}`}>{s.label}</button>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-adm-primary/20 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>

        <Card className="bg-adm-surface border-white/[0.06]">
          <CardContent className="p-6">

            {/* Step 0: Subscriber & Phase */}
            {step === 0 && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-adm-primary">Subscriber & Phase</h2>
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[250px]">
                    <Label>Subscriber</Label>
                    <Select value={form.subscriber_id} onValueChange={v => updateField('subscriber_id', v)}>
                      <SelectTrigger className="mt-1 bg-adm-bg border-white/[0.06]" data-testid="proto-subscriber-select"><SelectValue placeholder="Choose subscriber..." /></SelectTrigger>
                      <SelectContent className="bg-adm-surface border-white/[0.06]">{subscribers.map(s => (<SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="w-28">
                    <Label>Phase</Label>
                    <Input type="number" min={1} value={form.phase} onChange={e => updateField('phase', parseInt(e.target.value) || 1)} className="mt-1 bg-adm-bg border-white/[0.06]" data-testid="proto-phase-input" />
                  </div>
                </div>
                {existingProtocols.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-adm-primary/70 mb-2">Existing Protocols</h3>
                    <div className="space-y-2">
                      {existingProtocols.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-adm-bg border border-white/[0.06]">
                          <div>
                            <span className="text-sm text-adm-primary/80">Phase {p.phase} v{p.version}</span>
                            <Badge className={`ml-2 text-xs ${p.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/40'}`}>{p.status}</Badge>
                          </div>
                          {p.status === 'active' && (
                            <Button variant="ghost" size="sm" className="text-amber-500" onClick={() => handleArchive(p.id)}>
                              <Archive className="w-4 h-4 mr-1" /> Archive
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Protocol Source */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-adm-primary">Protocol Source</h2>

                {!sourceMode && !extraction && !execExtraction && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="source-mode-selection">
                    <button onClick={() => setSourceMode('executable')}
                      className="p-5 rounded-xl border border-[#4E7A6B]/30 hover:border-[#4E7A6B]/60 bg-[#4E7A6B]/5 hover:bg-[#4E7A6B]/10 transition-all text-left group"
                      data-testid="source-executable-btn">
                      <Sparkles className="w-7 h-7 text-[#4E7A6B] mb-3 group-hover:scale-110 transition-transform" />
                      <h3 className="text-sm font-semibold text-white/90 mb-1">Upload Schedule</h3>
                      <p className="text-[11px] text-white/40">PDF/image with structured schedule</p>
                      <Badge className="mt-2 text-[9px] bg-[#C89B7B]/10 text-[#C89B7B] border-[#C89B7B]/20"><Sparkles className="w-2.5 h-2.5 mr-1" /> Recommended</Badge>
                    </button>

                    <button onClick={() => setSourceMode('paste')}
                      className="p-5 rounded-xl border border-[#C89B7B]/30 hover:border-[#C89B7B]/60 bg-[#C89B7B]/5 hover:bg-[#C89B7B]/10 transition-all text-left group"
                      data-testid="source-paste-btn">
                      <ClipboardPaste className="w-7 h-7 text-[#C89B7B] mb-3 group-hover:scale-110 transition-transform" />
                      <h3 className="text-sm font-semibold text-white/90 mb-1">Paste Protocol</h3>
                      <p className="text-[11px] text-white/40">CSV, pipe, or tab-separated data</p>
                      <Badge className="mt-2 text-[9px] bg-[#4E7A6B]/10 text-[#4E7A6B] border-[#4E7A6B]/20">Fallback</Badge>
                    </button>

                    <button onClick={() => setSourceMode('upload')}
                      className="p-5 rounded-xl border border-white/10 hover:border-[#4E7A6B]/40 hover:bg-[#4E7A6B]/5 transition-all text-left group"
                      data-testid="source-upload-btn">
                      <Upload className="w-7 h-7 text-white/40 mb-3 group-hover:scale-110 transition-transform" />
                      <h3 className="text-sm font-semibold text-white/90 mb-1">Upload Sheet</h3>
                      <p className="text-[11px] text-white/40">Formulation sheet for wizard items</p>
                    </button>

                    <button onClick={() => { setSourceMode('manual'); setStep(2); }}
                      className="p-5 rounded-xl border border-white/10 hover:border-white/20 hover:bg-adm-surface/[0.02] transition-all text-left group"
                      data-testid="source-manual-btn">
                      <Edit3 className="w-7 h-7 text-white/40 mb-3 group-hover:scale-110 transition-transform" />
                      <h3 className="text-sm font-semibold text-white/90 mb-1">Build Manually</h3>
                      <p className="text-[11px] text-white/40">Add items one by one</p>
                    </button>
                  </div>
                )}

                {/* Paste mode */}
                {sourceMode === 'paste' && (
                  <PasteProtocol onParsed={handlePasteConfirm} onBack={() => setSourceMode(null)} />
                )}

                {/* Executable upload mode */}
                {sourceMode === 'executable' && !execExtraction && (
                  <div className="space-y-3">
                    <div className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all hover:border-[#4E7A6B]/50 border-white/10 ${savingExec ? 'pointer-events-none opacity-60' : ''}`}
                      onClick={() => { if (!savingExec) document.getElementById('exec-file-input')?.click(); }} data-testid="exec-upload-zone">
                      <input id="exec-file-input" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={e => handleExecUpload(e.target.files?.[0])} />
                      {savingExec ? (
                        <div className="space-y-3"><Loader2 className="w-10 h-10 text-[#4E7A6B] animate-spin mx-auto" /><p className="text-white/70">Parsing schedule...</p></div>
                      ) : (
                        <div className="space-y-3"><Upload className="w-10 h-10 text-[#4E7A6B]/50 mx-auto" /><p className="text-white/70 font-medium">Drop final schedule here</p><p className="text-white/40 text-xs">PDF or image</p></div>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSourceMode(null)} className="text-white/40 text-xs"><ChevronLeft className="w-3 h-3 mr-1" /> Back</Button>
                  </div>
                )}

                {execExtraction && !remapMode && (
                  <ExecutablePreview
                    extraction={execExtraction}
                    onSave={handleSaveExecutable}
                    saving={savingExec}
                    onDiscard={() => { setExecExtraction(null); setSourceMode(null); }}
                    onRemap={() => setRemapMode(true)}
                  />
                )}

                {remapMode && execExtraction && (
                  <ColumnRemapper
                    rawRows={execExtraction.raw_rows || []}
                    rawHeaders={execExtraction.raw_headers || []}
                    onComplete={handleRemapComplete}
                    onBack={() => setRemapMode(false)}
                  />
                )}

                {sourceMode === 'upload' && !extraction && (
                  <div className="space-y-3">
                    <ProtocolUploader subscriberId={form.subscriber_id} onExtracted={handleExtracted} />
                    <Button variant="ghost" size="sm" onClick={() => setSourceMode(null)} className="text-white/40 text-xs"><ChevronLeft className="w-3 h-3 mr-1" /> Back</Button>
                  </div>
                )}

                {extraction && (
                  <ExtractionPreview extraction={extraction} onConfirm={handleConfirmExtraction} onDiscard={() => { setExtraction(null); setSourceMode(null); }} />
                )}
              </div>
            )}

            {/* Step 2: Protocol Items */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-adm-primary">
                    Protocol Items
                    {form.items.filter(i => i.title.trim()).length > 0 && (
                      <Badge className="ml-2 text-xs bg-[#4E7A6B]/20 text-[#4E7A6B]">{form.items.filter(i => i.title.trim()).length} items</Badge>
                    )}
                  </h2>
                  <Button variant="outline" size="sm" onClick={addItem} className="border-white/[0.06]" data-testid="add-proto-item-btn"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
                </div>
                {form.items.map((item, idx) => (
                  <Card key={idx} className="bg-adm-bg border-white/[0.06]">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge className="bg-[#C89B7B]/20 text-[#C89B7B] text-xs">Item {idx + 1}</Badge>
                        {form.items.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-red-400 h-7"><Trash2 className="w-3 h-3" /></Button>}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div><Label className="text-xs">Code</Label><Input value={item.code} onChange={e => updateItem(idx, 'code', e.target.value)} className="mt-1 bg-adm-surface border-white/[0.06] h-8 text-sm" placeholder="e.g. TF-001" data-testid={`proto-item-code-${idx}`} /></div>
                        <div><Label className="text-xs">Title</Label><Input value={item.title} onChange={e => updateItem(idx, 'title', e.target.value)} className="mt-1 bg-adm-surface border-white/[0.06] h-8 text-sm" placeholder="Item title" data-testid={`proto-item-title-${idx}`} /></div>
                        <div><Label className="text-xs">Domain</Label>
                          <Select value={item.domain} onValueChange={v => updateItem(idx, 'domain', v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm bg-adm-surface border-white/[0.06]"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-adm-surface border-white/[0.06]">{DOMAINS.map(d => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}</SelectContent>
                          </Select></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Timing</Label><Input value={item.timing} onChange={e => updateItem(idx, 'timing', e.target.value)} className="mt-1 bg-adm-surface border-white/[0.06] h-8 text-sm" placeholder="e.g. Morning, before breakfast" /></div>
                        <div><Label className="text-xs">Visibility</Label>
                          <Select value={item.visibility} onValueChange={v => updateItem(idx, 'visibility', v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm bg-adm-surface border-white/[0.06]"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-adm-surface border-white/[0.06]">
                              <SelectItem value="both">Subscriber + Care Team</SelectItem>
                              <SelectItem value="subscriber">Subscriber Only</SelectItem>
                              <SelectItem value="care_team">Care Team Only</SelectItem>
                            </SelectContent>
                          </Select></div>
                      </div>
                      <div><Label className="text-xs">Instructions</Label><Textarea value={item.instruction} onChange={e => updateItem(idx, 'instruction', e.target.value)} className="mt-1 bg-adm-surface border-white/[0.06] min-h-[60px] text-sm" placeholder="Detailed instructions..." /></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Step 3: Goals & Dates */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-adm-primary">Goals & Timeline</h2>
                <div>
                  <Label>Phase Goals</Label>
                  {form.goals.map((g, i) => (
                    <div key={i} className="flex gap-2 mt-2">
                      <Input value={g} onChange={e => updateGoal(i, e.target.value)} className="bg-adm-bg border-white/[0.06]" placeholder={`Goal ${i + 1}`} data-testid={`proto-goal-${i}`} />
                      {form.goals.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeGoal(i)}><X className="w-4 h-4" /></Button>}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="mt-2" onClick={addGoal}><Plus className="w-3 h-3 mr-1" /> Add Goal</Button>
                </div>
                <div className="flex gap-4">
                  <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => updateField('start_date', e.target.value)} className="mt-1 bg-adm-bg border-white/[0.06]" data-testid="proto-start-date" /></div>
                  <div><Label>End Date (optional)</Label><Input type="date" value={form.end_date} onChange={e => updateField('end_date', e.target.value)} className="mt-1 bg-adm-bg border-white/[0.06]" data-testid="proto-end-date" /></div>
                </div>
              </div>
            )}

            {/* Step 4: Review & Publish */}
            {step === 4 && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-adm-primary">Review & Publish</h2>

                {/* Summary cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-adm-bg border border-white/[0.06]">
                    <p className="text-xs text-adm-primary/40 uppercase tracking-wider mb-1">Subscriber</p>
                    <p className="text-sm text-adm-primary/80">{selectedSub?.name || '—'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-adm-bg border border-white/[0.06]">
                    <p className="text-xs text-adm-primary/40 uppercase tracking-wider mb-1">Phase & Timeline</p>
                    <p className="text-sm text-adm-primary/80">Phase {form.phase} | {form.start_date} {form.end_date ? `to ${form.end_date}` : ''}</p>
                  </div>
                </div>

                {/* Items table */}
                <div>
                  <h3 className="text-sm font-medium text-adm-primary/70 mb-2">Protocol Items ({form.items.filter(i => i.title.trim()).length})</h3>
                  <div className="rounded-lg border border-white/[0.06] overflow-hidden">
                    <Table>
                      <TableHeader><TableRow className="border-white/[0.06]">
                        <TableHead className="text-xs">Code</TableHead><TableHead className="text-xs">Title</TableHead>
                        <TableHead className="text-xs">Domain</TableHead><TableHead className="text-xs">Timing</TableHead>
                        <TableHead className="text-xs">Visibility</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {form.items.filter(i => i.title.trim()).map((item, i) => (
                          <TableRow key={i} className="border-white/[0.06]">
                            <TableCell className="text-xs font-mono">{item.code}</TableCell>
                            <TableCell className="text-sm text-adm-primary/80">{item.title}</TableCell>
                            <TableCell><Badge className="text-xs bg-[#4E7A6B]/20 text-[#4E7A6B]">{DOMAINS.find(d => d.id === item.domain)?.label}</Badge></TableCell>
                            <TableCell className="text-xs text-adm-primary/50">{item.timing}</TableCell>
                            <TableCell className="text-xs text-adm-primary/50 capitalize">{item.visibility?.replace('_', ' ')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Goals */}
                <div>
                  <h3 className="text-sm font-medium text-adm-primary/70 mb-2">Goals</h3>
                  <ul className="space-y-1">
                    {form.goals.filter(g => g.trim()).map((g, i) => (
                      <li key={i} className="text-sm text-adm-primary/60 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#4E7A6B] flex-shrink-0 mt-0.5" />{g}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Publish / Confirm */}
                {showConfirm ? (
                  <PublishConfirmation form={form} selectedSub={selectedSub} onConfirm={handlePublish} onCancel={() => setShowConfirm(false)} publishing={publishing} />
                ) : (
                  <div className="flex justify-end pt-4">
                    <Button onClick={() => setShowConfirm(true)} disabled={!form.items.some(i => i.title.trim())} className="bg-[#4E7A6B] hover:bg-[#3D695B] text-white px-8" data-testid="proto-publish-btn">
                      <CheckCircle className="w-4 h-4 mr-2" /> Review & Publish
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="border-white/[0.06]" data-testid="proto-prev-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          {step < STEPS.length - 1 && (
            <Button onClick={() => setStep(s => s + 1)} className="bg-[#4E7A6B] hover:bg-[#3D695B] text-white" data-testid="proto-next-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
